import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Params } from '@angular/router';

import { Attribute, CategoryFilter, ListTruncation, Promotion, PromotionFilterChangedPayload } from '../../models';
import * as fromFilters from '../../store';
import { SelectChildCategoryStorePayload } from '../../store';
import { FiltersService } from '~app/modules/filters/services/filters.service';
import { FilterCollapseStateManager } from '~app/modules/filters/classes';
import { ResetButtonStateUtil } from '~app/modules/filters/utils/reset-button-state.util';
import { FILTER_ATTRIBUTE_KEYS, FILTER_COLLAPSE_STATE_KEYS } from '../../constants';
import { AttributeOption, PriceFilterRange, RangeFilters } from '~shared/models';
import { Category } from '~app/modules/generic/models';
import { ApplicationEventFilterAreaInteractionType } from '~shared/modules/application-event/constants';
import { VolumePrice } from '~app/modules/filters/models/volume-price.model';
import { map } from 'rxjs/operators';
import { SpecificFilterTypeConfig } from '~shared/services/specific-filter-bar.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { EXPERIMENT_VARIATIONS } from '~shared/constants';
import { OptimizelyService } from '~shared/services/optimizely.service';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';
import { TooltipPositions } from '~shared/modules/tooltip/models/tooltip-positions.model';
import { BlackDeal } from '~app/modules/filters/models/black-deal.model';
import { blackDealFilterChangedPayload } from '~app/modules/filters/models/black-deal-filter-changed-payload.model';

@Component({
  selector: 'app-search-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
})
export class FiltersComponent implements OnInit, OnChanges {
  @Output() filtersClosed: EventEmitter<ApplicationEventFilterAreaInteractionType> =
    new EventEmitter<ApplicationEventFilterAreaInteractionType>();

  @Output() specificFilterClosed: EventEmitter<ApplicationEventFilterAreaInteractionType> =
    new EventEmitter<ApplicationEventFilterAreaInteractionType>();

  @Input() queryParams: Params;
  @Input() productsCount: number;
  @Input() isMobile: boolean;
  @Input() isDesktop: boolean;
  @Input() isTablet: boolean;
  @Input() isFilterOpen: boolean;
  @Input() isZeroResult: boolean;
  @Input() isCategoryPage: boolean = false;
  @Input() isCustomPLPPage: boolean = false;
  @Input() isSearchPage: boolean = false;
  @Input() isBrandPLPPage: boolean = false;
  @Input() isCategoryListFilter: boolean;
  @Input() categories: Category[];
  @Input() isMobileOrTablet: boolean;
  @Input() filterType: SpecificFilterTypeConfig;

  searchResultCategories$: Observable<Category[]>;
  blackDealFilter$: Observable<BlackDeal>;
  promotion$: Observable<Promotion>;
  volumePrice$: Observable<VolumePrice>;
  rebuyGuarantee$: Observable<AttributeOption>;
  attributes$: Observable<Attribute[]>;
  isFiltersLoading$: Observable<boolean>;
  categoriesFilters$: Observable<string[]>;
  attributeFilters$: Observable<{ [key: string]: string[] | number[] }[]>;
  filterCollapseStateKeys = FILTER_COLLAPSE_STATE_KEYS;
  selectedCategory$: Observable<any>;
  selectedChildCategory$: Observable<{ resultsCategories: Category[]; filteredCategories: string[] }>;
  isVariantB$: Observable<boolean>;
  isPLP: boolean;
  isNewPLPVariantRestyle$: Observable<boolean>;
  tooltipPosition = TooltipPositions.top;
  bulletBackgroundColor = '#E5EAF1';

  attributeFilters;
  limitOption = ListTruncation.mobileShowMoreCount;
  maxAttrOptions = ListTruncation.mobileShowMoreCount;

  constructor(
    private store: Store<fromFilters.State>,
    private filtersService: FiltersService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private optimizelyService: OptimizelyService,
    private plpVariantRestyleService: PlpVariantRestyleService
  ) {}

  ngOnInit(): void {
    this.blackDealFilter$ = this.store.pipe(select(fromFilters.getBlackDealFilters));
    this.promotion$ = this.store.pipe(select(fromFilters.getPromotion));
    this.volumePrice$ = this.store.pipe(select(fromFilters.getVolumePrice));
    this.rebuyGuarantee$ = this.store.pipe(select(fromFilters.getRebuyGuarantee));
    this.attributeFilters$ = this.store.pipe(select(fromFilters.getAttributeFilters));
    this.isFiltersLoading$ = this.store.pipe(select(fromFilters.isFiltersLoading));
    this.searchResultCategories$ = this.store.pipe(select(fromFilters.getCategories));
    this.categoriesFilters$ = this.store.pipe(select(fromFilters.getCategoriesFilters));
    this.selectedChildCategory$ = this.store.pipe(select(fromFilters.getSelectedChildCategory));
    this.selectedCategory$ = this.selectedChildCategory$.pipe(
      map((selectChildCategory: SelectChildCategoryStorePayload) => {
        const firstFilteredCategorySlug: string = selectChildCategory?.filteredCategories[0];

        if (!firstFilteredCategorySlug) {
          return null;
        }

        return selectChildCategory?.resultsCategories
          .find((parentCategory: Category) =>
            parentCategory.children.some((childCategory: Category) => childCategory.slug === firstFilteredCategorySlug)
          )
          ?.children.find((childCategory: Category) => childCategory.slug === firstFilteredCategorySlug);
      })
    );

    if (this.filterType) {
      this.attributes$ = this.store.pipe(
        select(fromFilters.getAttributes),
        map((attributes: Attribute[]) => {
          const attr: Attribute[] = attributes?.filter(
            (attribute: Attribute) =>
              this.filterType?.type === 'attribute' && attribute?.id === this.filterType?.options?.attribute.id
          );

          return attr;
        })
      );
    } else {
      this.attributes$ = this.store.pipe(select(fromFilters.getAttributes));
    }

    this.store
      .pipe(select(fromFilters.getAttributeFilters))
      .subscribe((attributeFilters: { [key: string]: string[] | number[] }[]) => {
        this.attributeFilters = attributeFilters;
      });
    this.isVariantB$ = this.optimizelyService
      .getExperimentVariant('category_filter_redesign')
      .pipe(map((variant: EXPERIMENT_VARIATIONS) => variant === EXPERIMENT_VARIATIONS.VARIANT_ONE));
    this.isNewPLPVariantRestyle$ = this.plpVariantRestyleService.isNewPLPVariantRestyle();
    this.isPLP = this.plpVariantRestyleService.isPLP();
    if (!this.isMobileOrTablet) {
      this.limitOption = ListTruncation.desktopShowMoreCount;
      this.maxAttrOptions = ListTruncation.desktopShowAllCount;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isFilterOpen'] && changes['isFilterOpen'].currentValue) {
      this.filtersService.saveMobileState(this.queryParams.filter);
    } else if (changes['queryParams']) {
      this.filtersService.updateQueryParams(this.queryParams);
    }
  }

  /* Collapse Methods */

  updateFilterCollapseState(key: string, expanded: boolean): void {
    FilterCollapseStateManager.instance.update(key, expanded);
  }

  isFilterCollapseExpanded(key: string): boolean {
    return FilterCollapseStateManager.instance.isExpanded(key);
  }

  /* Apply Methods */

  filterByPrice(priceRanges: PriceFilterRange): void {
    this.filtersService.filterByPrice(priceRanges);
  }

  filterByCategories(categoriesSlugs: CategoryFilter[]): void {
    this.filtersService.filterByCategories(categoriesSlugs);
  }

  filterByBlackDeals(blackDeal: blackDealFilterChangedPayload): void {
    this.filtersService.filterByBlackDeals(blackDeal);
  }

  filterByPromotion(promotion: PromotionFilterChangedPayload): void {
    this.filtersService.filterByPromotion(promotion);
  }

  filterByVolumePrice(volumePrice: PromotionFilterChangedPayload): void {
    this.filtersService.filterByVolumePrice(volumePrice);
  }

  filterByRebuyGuarantee(rebuyGuaranteeAttrOption: AttributeOption): void {
    const currentlySelectedAttributes = this.queryParams?.filter?.options;
    let selectedCharacteristics: string[] = currentlySelectedAttributes?.characteristics
      ? Object.values(currentlySelectedAttributes?.characteristics)
      : [];

    if (!!rebuyGuaranteeAttrOption) {
      selectedCharacteristics?.push(rebuyGuaranteeAttrOption?.id);
    } else {
      selectedCharacteristics = selectedCharacteristics?.filter(
        (attrId: string) => attrId !== FILTER_ATTRIBUTE_KEYS.REBUY_GUARANTEE_OPTION
      );
    }

    const characteristics = selectedCharacteristics.reduce((obj, currentValue, index) => {
      obj[index] = currentValue;
      return obj;
    }, {});
    const attrs = {
      selectedAttrs: {
        ...currentlySelectedAttributes,
        characteristics,
      },
      content: !!rebuyGuaranteeAttrOption ? rebuyGuaranteeAttrOption?.id : '',
      attributeId: FILTER_ATTRIBUTE_KEYS.CHARACTERISTICS,
    };
    this.filtersService.filterByAttribute(attrs, FILTER_ATTRIBUTE_KEYS.CHARACTERISTICS);
  }

  filterByAttribute(attrs: { [key: string]: any; content: string; attributeId: string }): void {
    const rebuyGuaranteeId: string = this.queryParams?.filter?.options?.characteristics?.find(
      (characteristicId: string) => characteristicId === 'int_rbg'
    );
    let characteristicsList: string[] = attrs?.selectedAttrs?.characteristics
      ? Object.values(attrs?.selectedAttrs?.characteristics)
      : [];

    if (rebuyGuaranteeId) {
      characteristicsList?.push(rebuyGuaranteeId);
    }

    const selectedAttrs = {
      ...attrs?.selectedAttrs,
      ...(characteristicsList?.length
        ? {
            characteristics: characteristicsList?.reduce((obj, currentValue: string, index: number) => {
              obj[index] = currentValue;
              return obj;
            }, {}),
          }
        : undefined),
    };

    const objectINeed = {
      attributeId: attrs?.attributeId,
      content: attrs?.content,
      ...(Object?.keys(selectedAttrs)?.length ? { selectedAttrs } : undefined),
    };

    this.filtersService.filterByAttribute(objectINeed, attrs?.attributeId);
  }

  filterByAttributeRange(rangeFilters: RangeFilters): void {
    this.filtersService.filterByRange(rangeFilters);
  }

  /* Reset button methods */

  isResetButtonShown(key: string): boolean {
    return !ResetButtonStateUtil.isHidden(key, this.queryParams);
  }

  isCategoryResetButtonShown(): boolean {
    return !ResetButtonStateUtil.isHiddenForCategoriesFilter(this.queryParams);
  }

  /* Reset Methods */

  resetCategoryFilter(): void {
    this.filtersService.resetCategoryFilter();
  }

  resetAttributeFilter(attrId: string): void {
    this.filtersService.resetAttributeFilter(attrId);
  }

  closeAndResetFilterToPreviousState(): void {
    this.filtersClosed.emit(ApplicationEventFilterAreaInteractionType.Apply);
    this.filtersService.resetFilterToPreviousState(this.queryParams);
  }

  closeAndResetSpecificFilterToPreviousState(): void {
    this.specificFilterClosed.emit(ApplicationEventFilterAreaInteractionType.Apply);
    this.filtersService.resetFilterToPreviousState(this.queryParams);
  }

  applyAndCloseFilters(): void {
    this.filtersClosed.emit(ApplicationEventFilterAreaInteractionType.Apply);
  }

  applyAndCloseSpecificFilter(): void {
    this.specificFilterClosed.emit(ApplicationEventFilterAreaInteractionType.Apply);
  }
}
